import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";

import TableContainer from "components/Common/TableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import {
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  mergeModalSubmitIntoListFilter,
} from "helpers/metadataFilterHelpers";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { submitAiRecruiterLogFilterData } from "model/AiRecruiterLogModel";
import { useAiRecruiterLogContext } from "libs/context/AiRecruiterLogContext";
import Loaders from "components/Common/Loaders";
import { toast } from "react-toastify";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";
import { useCallExecutiveAppliedFilter } from "hooks/useCallExecutiveAppliedFilter";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";



const defaultTableColumns = [
  { Header: "Id", accessor: "id" },
  { Header: "Candidate Id", accessor: "candidateId" },
  { Header: "Client", accessor: "client" },
  { Header: "Notification Type", accessor: "notificationType" },
  { Header: "Created On", accessor: "createdOn" },
];

const normalizeAiRecruiterLogFilterPayload = (raw = {}) => {
  const out = { ...raw };
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) {
      delete out[key];
      return;
    }
    const value = out[key];
    if (value === "" || value === undefined) out[key] = null;
  });
  return out;
};

const extractList = (response) => {
  if (response == null) return [];
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.records)) return raw.records;
  if (raw && Array.isArray(raw.data)) return raw.data;
  return [];
};

const getPaginationEnvelope = (response) => {
  const raw = response?.data;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  return {};
};

const buildPaginationMeta = (envelope, payload, recordsLength) => ({
  pageNumber: Number(envelope?.pageNumber) || Number(payload?.pageNumber) || 1,
  pageSize: Number(envelope?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
  totalPages: Number(envelope?.totalPages) || 0,
  totalRecords: Number(envelope?.totalRecords) || recordsLength,
});

const defaultChatUserAvatarSrc = `${process.env.PUBLIC_URL || ""}/no-image.png`;

const getRowId = (row = {}) => String(row?.id ?? row?._id ?? "").trim();

const getTranscriptText = (row = {}) => {
  const v = row?.callTranscript ?? row?.calltranscript ?? "";
  if (v == null) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const getRawJsonDisplay = (row = {}) => {
  const blob = row?.rawJson ?? row?.rawjson;
  if (blob != null) {
    if (typeof blob === "string") {
      try {
        return JSON.stringify(JSON.parse(blob), null, 2);
      } catch {
        return blob;
      }
    }
    try {
      return JSON.stringify(blob, null, 2);
    } catch {
      return String(blob);
    }
  }
  return "";
};

const parseTranscriptMessages = (row = {}) => {
  const raw = row?.callTranscript ?? row?.calltranscript;
  if (raw == null || raw === "") return [];

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  const list = Array.isArray(parsed)
    ? parsed
    : parsed?.messages ?? parsed?.data ?? parsed?.records ?? [];
  if (!Array.isArray(list)) return [];

  return list
    .map((item = {}, idx) => {
      const roleRaw = String(item?.role ?? item?.sender ?? item?.from ?? "bot")
        .trim()
        .toLowerCase();
      const message = String(
        item?.message ?? item?.text ?? item?.content ?? ""
      ).trim();
      if (!message) return null;
      return {
        id: item?.id ?? `${roleRaw || "msg"}-${idx}`,
        role: roleRaw === "user" ? "user" : "bot",
        message,
      };
    })
    .filter(Boolean);
};

const AiRecruiterLog = () => {
  const { fetchAiRecruiterLogContextData, aiRecruiterLogContextData } =
    useAiRecruiterLogContext() || {};

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [aiRecruiterListFilter, setAiRecruiterListFilter] = useState({});
  const {
    commitDraftAsApplied,
    resolveFilterBase,
    setAppliedFilter,
  } = useCallExecutiveAppliedFilter();
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("AiRecruiterLog");
  const initialFilterFetchedRef = useRef(false);
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const {
    shouldShowTableHeaderFilterAndResetControls,
    shouldRenderMetadataToolbarFilterRow,
    activeModalFilterCount,
    modalFilterData: aiRecruiterLogFilterModalData,
    metadataToolbarFilterDefinitions,
    metadataFiltersAll,
    usesMetadataDrivenFilterUi,
    buildBaselineEmptyListFilter,
    toolbarDropdownReactSelectOptionsByFieldName,
  } = useMetadataDrivenFilters(
    aiRecruiterLogContextData?.filters,
    aiRecruiterListFilter,
    "AiRecruiterLog"
  );

  const [transcriptModal, setTranscriptModal] = useState({ open: false, row: null });
  const [rawJsonModal, setRawJsonModal] = useState({ open: false, row: null });

  const transcriptBody = useMemo(
    () => (transcriptModal.row ? getTranscriptText(transcriptModal.row) : ""),
    [transcriptModal.row]
  );
  const transcriptMessages = useMemo(
    () =>
      transcriptModal.row ? parseTranscriptMessages(transcriptModal.row) : [],
    [transcriptModal.row]
  );
  const rawJsonBody = useMemo(
    () => (rawJsonModal.row ? getRawJsonDisplay(rawJsonModal.row) : ""),
    [rawJsonModal.row]
  );


  useEffect(() => {
    const metadataFilters = aiRecruiterLogContextData?.filters;
    if (!Array.isArray(metadataFilters) || !metadataFilters.length) return;
    setAiRecruiterListFilter((previousFilterState) => {
      const mergedFilterState = { ...previousFilterState };
      let mergedNewKeysIntoState = false;
      metadataFilters.forEach((filterDefinitionRow) => {
        const fieldKey = filterDefinitionRow?.field;
        if (!fieldKey) return;
        if (!(fieldKey in mergedFilterState)) {
          mergedFilterState[fieldKey] =
            filterDefinitionRow?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(fieldKey);
          if (!(asyncLabelFieldKey in mergedFilterState)) {
            mergedFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
    });
  }, [aiRecruiterLogContextData?.filters]);

  /** Table columns from context — same mapping as `job-category.jsx`. */
  useEffect(() => {
    const cols = aiRecruiterLogContextData?.table?.columns?.map((col) => ({
      Header: col?.title,
      accessor: col?.field,
      type: col?.type,
    }));
    setTableMetaData(cols?.length ? cols : undefined);
  }, [aiRecruiterLogContextData]);

  const loadList = useCallback(
    async (overrides = {}, filterOverride) => {
      const filterBase = resolveFilterBase(filterOverride);
      setLoading(true);
      try {
        const payload = normalizeAiRecruiterLogFilterPayload({
          pageNumber: 1,
          pageSize: DEFAULT_PAGE_SIZE,
          ...filterBase,
          ...overrides,
        });
        const response = await submitAiRecruiterLogFilterData(payload);
        const records = extractList(response);
        const mapped = (Array.isArray(records) ? records : []).map((item = {}) => ({
          ...item,
          id: item?.id ?? item?._id ?? getRowId(item) ?? "",
        }));

        if (response == null || response?.success === false) {
          setRows([]);
          if (response?.message) toast.error(response.message);
          return;
        }

        setRows(mapped);
        const envelope = getPaginationEnvelope(response);
        setPaginationMeta(buildPaginationMeta(envelope, payload, mapped.length));
      } catch (err) {
        toast.error(err?.message || "Unable to load AI recruiter log");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [resolveFilterBase]
  );

  const loadListRef = useRef(loadList);
  loadListRef.current = loadList;

  /** Initial load: context metadata, then list once settings page size is ready. */
  useEffect(() => {
    fetchAiRecruiterLogContextData?.();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    setAppliedFilter({});
    loadListRef.current({ pageNumber: 1, pageSize }, {});
  }, [settingsReady, settingsPageSize, setAppliedFilter]);

  const handleFilterReset = useCallback(async () => {
    const emptyFilter = buildBaselineEmptyListFilter();
    setAiRecruiterListFilter(emptyFilter);
    setAppliedFilter(emptyFilter);
    setCommonFilterModalOpen(false);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      emptyFilter
    );
  }, [buildBaselineEmptyListFilter, loadList, paginationMeta.pageSize, setAppliedFilter]);

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleFilterReset();
      setCommonResetFilter(false);
    };
    run();
  }, [commonResetFilter, handleFilterReset]);

  const handleToolbarTextFilterInputChange = (fieldKey, nextValue) => {
    setAiRecruiterListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: nextValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (fieldKey, selectedOption) => {
    setAiRecruiterListFilter((previousFilterState) => ({
      ...previousFilterState,
      [fieldKey]: selectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(fieldKey)]: selectedOption?.label ?? "",
    }));
  };

  const handleUpperToolbarFilterSearch = async () => {
    commitDraftAsApplied(aiRecruiterListFilter);
    await loadList({
      pageNumber: 1,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleFilterSubmit = async (formData = {}) => {
    const mergedFilter = mergeModalSubmitIntoListFilter(
      aiRecruiterListFilter,
      aiRecruiterLogFilterModalData,
      formData
    );
    setAiRecruiterListFilter(mergedFilter);
    setCommonFilterModalOpen(false);
    commitDraftAsApplied(mergedFilter);
    await loadList(
      {
        pageNumber: 1,
        pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
      },
      mergedFilter
    );
  };

  const handlePageChange = async (page) => {
    await loadList({
      pageNumber: page,
      pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

  const handleRowsPerPageChange = async (size) => {
    await loadList({ pageNumber: 1, pageSize: size });
  };

  const effectiveColumns = tableMetaData?.length ? tableMetaData : defaultTableColumns;

  return (
    <React.Fragment>
      <div className="page-content ai-recruiter-log-page">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h5 className="fw-bold mb-0">AI Recruiter Log</h5>
              </div>
              <hr />

              {shouldRenderMetadataToolbarFilterRow ? (
                <MetadataFilterToolbar
                  toolbarDefinitions={metadataToolbarFilterDefinitions}
                  listFilter={aiRecruiterListFilter}
                  onTextChange={handleToolbarTextFilterInputChange}
                  onSelectChange={handleToolbarReactSelectFilterChange}
                  onSearch={handleUpperToolbarFilterSearch}
                  onClear={handleFilterReset}
                  showClearWhenNoModalFilters={
                    !shouldShowTableHeaderFilterAndResetControls
                  }
                  toolbarDropdownOptionsByFieldName={
                    toolbarDropdownReactSelectOptionsByFieldName
                  }
                  pageName="AiRecruiterLog"
                />
              ) : null}

              {loading ? (
                <Loaders />
              ) : effectiveColumns?.length ? (
                <TableContainer
                  columns={effectiveColumns}
                  data={rows}
                  isActionRequired
                  isCheckboxLeftSide={false}
                  isPagination
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  pageName="AiRecruiterLog"
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  setCommonResetFilter={setCommonResetFilter}
                  allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                  activeFilterCount={activeModalFilterCount}
                  onAiRecruiterCallTranscript={(row) => setTranscriptModal({ open: true, row })}
                  onAiRecruiterRawJson={(row) => setRawJsonModal({ open: true, row })}
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="AI Recruiter Log"
            handleSubmit={handleFilterSubmit}
            filterData={aiRecruiterLogFilterModalData}
            pageName="AiRecruiterLog"
            metadataFilterDefinitions={metadataFiltersAll}
            enableFilterToolbarCustomization={usesMetadataDrivenFilterUi}
            commonResetFilter={commonResetFilter}
            setCommonResetFilter={setCommonResetFilter}
          />
        </Container>
      </div>

      <Modal
        isOpen={transcriptModal.open}
        toggle={() => setTranscriptModal({ open: false, row: null })}
        size="lg"
        className="modal-full-height"
        centered
      >
        <ModalHeader toggle={() => setTranscriptModal({ open: false, row: null })}>
          Call transcript
        </ModalHeader>
        <ModalBody className="ai-recruiter-transcript-modal-body">
          {transcriptMessages.length ? (
            <div className="ai-recruiter-transcript-chat">
              {transcriptMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`ai-recruiter-chat-row ai-recruiter-chat-row--${msg.role}`}
                >
                  <div
                    className={`ai-recruiter-chat-avatar ai-recruiter-chat-avatar--${msg.role}`}
                    aria-hidden
                  >
                    {msg.role === "user" ? (
                      <img
                        src={defaultChatUserAvatarSrc}
                        alt=""
                        width={38}
                        height={38}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <i className="mdi mdi-robot-outline" aria-hidden />
                    )}
                  </div>
                  <div className="ai-recruiter-chat-main">
                    <div className="ai-recruiter-chat-bubble">
                    <div
                      className={`ai-recruiter-sender-label ai-recruiter-sender-label--${msg.role}`}
                      aria-label={
                        msg.role === "user" ? "Message sent by candidate" : "Message sent by AI recruiter"
                      }
                    >
                      <span className="ai-recruiter-sender-dot" aria-hidden />
                      <span className="ai-recruiter-sender-text">
                        {msg.role === "user" ? "User" : "Bot"}
                      </span>
                    </div>
                    <div>
                        {msg.message}
                        </div>
                        </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <pre
              className="mb-0 p-3 rounded border bg-light small"
              style={{ maxHeight: "70vh", overflow: "auto", whiteSpace: "pre-wrap" }}
            >
              {transcriptBody || "No transcript available for this row."}
            </pre>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={rawJsonModal.open}
        toggle={() => setRawJsonModal({ open: false, row: null })}
        size="lg"
        className="modal-full-height"
        centered
      >
        <ModalHeader toggle={() => setRawJsonModal({ open: false, row: null })}>
          Raw JSON
        </ModalHeader>
        <ModalBody>
          <pre
            className="mb-0 p-3 rounded border bg-light small"
            style={{ maxHeight: "70vh", overflow: "auto", whiteSpace: "pre-wrap" }}
          >
            {rawJsonBody || "{}"}
          </pre>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

AiRecruiterLog.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AiRecruiterLog);
